import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import axiosInstance from './axiosInstance';

// Wortmarke wie auf der Login-Seite
function Wordmark() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'center', mb: 3 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'secondary.main' }} />
      <Typography component="span" sx={{ fontWeight: 800, fontSize: '2rem', letterSpacing: '-0.02em', lineHeight: 1, color: 'text.primary' }}>
        Kin<Box component="span" sx={{ color: 'primary.main' }}>flow</Box>
      </Typography>
    </Box>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await axiosInstance.post('/password-reset/', { email });
      // Server antwortet bewusst immer gleich (keine Konto-Enumeration).
      setSent(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Bitte eine gültige E-Mail-Adresse eingeben.');
      } else if (err.response?.status === 429) {
        setError('Zu viele Anfragen. Bitte versuche es später erneut.');
      } else {
        setError('Etwas ist schiefgelaufen. Bitte versuche es später erneut.');
      }
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: '0 10px 40px rgba(14,26,43,.08)',
          }}
        >
          <Wordmark />

          {sent ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                <MarkEmailReadOutlinedIcon sx={{ fontSize: 44, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700 }}>
                E-Mail unterwegs
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1.5, mb: 3 }}>
                Falls ein Konto mit <strong>{email}</strong> existiert, haben wir eine E-Mail mit
                einem Link zum Zurücksetzen des Passworts verschickt. Bitte prüfe auch deinen
                Spam-Ordner. Der Link ist aus Sicherheitsgründen nur begrenzt gültig.
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                fullWidth
                variant="contained"
                size="large"
                startIcon={<ArrowBackIcon />}
              >
                Zurück zur Anmeldung
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700 }}>
                Passwort vergessen?
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 3 }}>
                Gib deine E-Mail-Adresse ein — wir senden dir einen Link zum Zurücksetzen.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }} noValidate>
                <TextField
                  margin="normal" required fullWidth
                  id="email" label="E-Mail-Adresse" name="email" type="email"
                  autoComplete="email" autoFocus
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading} error={!!error}
                />
                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  sx={{ mt: 3, mb: 2 }} disabled={isLoading || !email}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Link senden'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Link component={RouterLink} to="/login" variant="body2" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <ArrowBackIcon fontSize="inherit" /> Zurück zur Anmeldung
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default ForgotPassword;
